import { useEffect, useRef, useState } from 'react';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  Flex,
  InputGroup,
  InputLeftElement,
  Input,
  Accordion,
  useDisclosure,
  useOutsideClick,
  Box,
  Icon,
  InputRightElement,
  Tooltip,
} from '@chakra-ui/react';
import { ChevronDownIcon } from '@heroicons/react/solid';
import { SearchMd } from '@kula-ai/icons/outline/SearchMd';
import AccordionWrapper from '@/ui/accordion/accordianSearchWrapper';
import { X } from '@kula-ai/icons/outline/X';
import { colors } from '@/theme/foundations/colors';
import { FILTER_KINDS } from '../../utils/constants';
import { KText } from '@/ui-library/components';
import { LuInfo } from 'react-icons/lu';

export const FilterRowLabelField = ({
  options,
  value,
  onChange,
  customFilters,
  previousSelectedFilter,
  filterConfig = [],
  accordionTitle = 'Application Filters',
  showAccordion,
  showSearchInput,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const { onClose, isOpen, onOpen } = useDisclosure();
  const ref = useRef();
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Compute selected value with custom filter label lookup
  const selectedValue = (() => {
    if (Object.keys(value).length === 0) {
      return 'Select';
    }

    // Check if value is a custom filter label (from user_preference restoration)
    const customFilterByLabel = customFilters?.find(
      (f) => f.label === value.value || f.label === value.label
    );

    if (customFilterByLabel) {
      return customFilterByLabel.label;
    }

    // Check if value is a custom filter ID
    const customFilterForValue = customFilters?.find(
      (f) =>
        f?.id === value?.value || f?.id === value?.label || f?.id === value?.id
    );

    if (customFilterForValue) {
      return customFilterForValue.label;
    }

    // Check if the value itself is in the filter config
    const configLabel = filterConfig[value.value]?.filterLabel;
    if (configLabel) {
      return configLabel;
    }

    // Fallback to label or value
    return value.label || value.value || 'Select';
  })();

  const filteredOptions = options.filter((key) => {
    // Check if the option is already selected in other filters
    const isAlreadySelected = previousSelectedFilter?.some(
      (filter) =>
        (filter.filter_by?.value === key || filter.filter_by?.label === key) &&
        filter.filter_by?.value !== value?.value
    );
    // For config filters, check the filterLabel
    const name = filterConfig[key]?.filterLabel?.toLowerCase();
    const matchesSearch = name?.includes(searchTerm?.toLowerCase());

    return matchesSearch && !isAlreadySelected;
  });

  const filteredCustomFields = customFilters?.filter((filter) => {
    // Basic filter conditions
    const isValidFilter =
      FILTER_KINDS?.includes(filter.kind) && !filter.deleted && !filter.multi;

    // Check if already selected in other filters (but not in current filter)
    const isAlreadySelected = previousSelectedFilter?.some(
      (f) =>
        f.filter_by?.value === filter.label && filter.label !== value?.value
    );

    // Check if matches search
    const label = filter?.label?.toLowerCase();
    const matchesSearch = label?.includes(searchTerm.toLowerCase());

    return isValidFilter && matchesSearch && !isAlreadySelected;
  });

  const handleCustomFilterChange = (customFilter) => {
    onChange({
      value: customFilter?.label,
      label: customFilter?.label,
      id: customFilter?.id, // Store the custom field ID for API calls
      isCustom: true, // Mark as custom filter
    });
    onClose();
  };

  const onSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  useEffect(() => {
    if (isOpen) {
      if (dropdownRef.current) {
        dropdownRef.current.scrollTop = 0;
      }
    }
  }, [isOpen]);

  useOutsideClick({
    ref: ref,
    handler: () => {
      if (isOpen) {
        onClose();
      }
    },
  });

  const clearSearch = () => {
    setSearchTerm('');
  };

  // When component mounts, normalize filters restored from user_preference and set defaults
  useEffect(() => {
    // Check if current value is a custom filter that needs normalization
    const matchingCustomFilterByLabel = customFilters?.find(
      (f) => f.label === value?.value || f.label === value?.label
    );

    const matchingCustomFilterById = customFilters?.find(
      (f) =>
        f.id === value?.value || // Using == to handle both string and number IDs
        f.id === value?.label
    );

    const matchingCustomFilter =
      matchingCustomFilterByLabel || matchingCustomFilterById;

    // If we found a custom filter, ensure it has the proper structure with ID
    if (matchingCustomFilter && !value?.id) {
      onChange({
        value: matchingCustomFilter.label,
        label: matchingCustomFilter.label,
        id: matchingCustomFilter.id,
        isCustom: true,
      });
      return;
    }

    // If filter has ID but no label (from restored state), populate the label
    if (value?.id && !value?.label) {
      const customFilterWithId = customFilters?.find(
        (f) => f?.id === value?.id
      );
      if (customFilterWithId) {
        onChange({
          ...value,
          value: customFilterWithId.label,
          label: customFilterWithId.label,
        });
        return;
      }
    }

    // If no value is selected, try to auto-select first available option
    if (
      !value?.value &&
      (filteredOptions.length > 0 || filteredCustomFields?.length > 0)
    ) {
      // Try config filters first
      if (filteredOptions.length > 0) {
        const firstOption = filteredOptions[0];
        onChange({
          value: firstOption,
          label: filterConfig[firstOption]?.filterLabel || firstOption,
        });
      }
      // If no config filters, try custom filters
      else if (filteredCustomFields?.length > 0) {
        const firstCustomFilter = filteredCustomFields[0];
        onChange({
          value: firstCustomFilter.label,
          label: firstCustomFilter.label,
          id: firstCustomFilter.id, // Store the custom field ID for API calls
          isCustom: true, // Mark as custom filter
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value?.value, value?.label, customFilters]);

  const renderTrigger = () => {
    return (
      <Flex
        cursor="pointer"
        px={4}
        py="9px"
        borderRadius="xs"
        border="1px solid"
        borderColor={'border-color'}
        _hover={{
          borderColor: 'secondary-300',
          background: 'secondary-25',
        }}
        _focus={{
          borderColor: 'secondary-300',
          background: 'secondary-25',
        }}
        justifyContent="space-between"
        alignItems="center"
        onClick={() => {
          if (isOpen) {
            onClose();
          } else {
            onOpen();
            setTimeout(() => inputRef?.current?.focus(), 100);
          }
        }}
      >
        <KText isTruncated maxW="calc(100% - 1px)" enableTooltip>
          {selectedValue}
        </KText>

        <ChevronDownIcon
          width="16px"
          height="16px"
          color={colors['secondary-900']}
        />
      </Flex>
    );
  };

  const renderSearchInput = () => {
    // Fills the dropdown rather than forcing it back to a fixed 350px.
    return (
      <Flex width={{ base: '100%', md: '350px' }} p="8px">
        <InputGroup alignItems="center">
          <InputLeftElement pointerEvents="none">
            <Icon as={SearchMd} color="secondary-800" width="18" h="100%" />
          </InputLeftElement>
          <Input
            ref={inputRef}
            placeholder="Search"
            onChange={onSearch}
            value={searchTerm}
            _placeholder={{
              opacity: 1,
              fontSize: 'sm',
              fontWeight: 'medium',
              color: 'secondary-400',
            }}
            bg="secondary-50"
            borderColor="secondary-200"
            _hover={{
              bg: 'secondary-50',
              borderColor: 'secondary-200',
            }}
            _focus={{
              bg: 'secondary-50',
              boxShadow: 'none !important',
              borderColor: 'secondary-200 !important',
            }}
          />
          <InputRightElement>
            {searchTerm && (
              <Icon cursor="pointer" as={X} onClick={clearSearch} />
            )}
          </InputRightElement>
        </InputGroup>
      </Flex>
    );
  };

  const renderFilterOptions = () => {
    if (filteredOptions.length === 0 && filteredCustomFields?.length === 0) {
      return (
        <Box textAlign="center" py="20px">
          <KText>No matching Filters found.</KText>
        </Box>
      );
    }

    return (
      <Flex
        direction="column"
        overflowY="auto"
        ref={dropdownRef}
        maxHeight="290px"
      >
        {filteredOptions.length > 0 && (
          <>
            {/*
              Group and render filter options by their sections (e.g., 'Contact filters', 'Job filters')
              If a filter doesn't have a section specified, it goes into the 'Default' section
            */}
            {(() => {
              // Create an object that groups filters by their section
              // Example: { 'Contact filters': ['skills', 'location'], 'Job filters': ['job_title'] }
              const groupedOptions: Record<string, string[]> =
                filteredOptions.reduce(
                  (acc, key) => {
                    // Get section from filterConfig, fallback to 'Default' if not specified
                    const section = filterConfig[key]?.section || 'Default';
                    if (!acc[section]) {
                      acc[section] = [];
                    }
                    acc[section].push(key);
                    return acc;
                  },
                  {} as Record<string, string[]>
                );

              // Render a separate accordion for each section
              return Object.entries(groupedOptions).map(
                ([section, sectionOptions]) => {
                  if (!showAccordion) {
                    return (
                      <Box key={section}>
                        {sectionOptions.map((key) => {
                          const {
                            filterLabel: label,
                            isDisabled,
                            disabledFilterInfoMessage,
                          } = filterConfig[key];
                          const isSelected = previousSelectedFilter?.some(
                            (filter) => filter.filter_by?.value === key
                          );

                          return (
                            <Box
                              key={key}
                              onClick={() => {
                                if (!isDisabled) {
                                  onChange({ label, value: key });
                                  onClose();
                                }
                              }}
                              px={6}
                              py={3}
                              borderBottom="1px solid"
                              borderColor="gray.100"
                              cursor={isDisabled ? 'not-allowed' : 'pointer'}
                              bg={
                                isSelected
                                  ? colors['hover-light']
                                  : 'transparent'
                              }
                              _hover={{
                                bg: isDisabled
                                  ? 'transparent'
                                  : colors['hover-light'],
                              }}
                              opacity={isDisabled ? 0.5 : 1}
                            >
                              <Flex
                                justify="flex-start"
                                align="center"
                                gap="12px"
                              >
                                <Flex align="center" gap={3}>
                                  <KText>{label}</KText>
                                </Flex>
                                {disabledFilterInfoMessage && (
                                  <Tooltip label={disabledFilterInfoMessage}>
                                    <Box>
                                      <Icon
                                        opacity={
                                          disabledFilterInfoMessage ? 0.5 : 1
                                        }
                                        as={LuInfo as any}
                                      />
                                    </Box>
                                  </Tooltip>
                                )}
                              </Flex>
                            </Box>
                          );
                        })}
                      </Box>
                    );
                  }
                  return (
                    <AccordionWrapper
                      key={section}
                      // Use provided accordionTitle for 'Default' section, otherwise use section name
                      title={section === 'Default' ? accordionTitle : section}
                      count={sectionOptions?.length}
                      // Transform filter keys into option objects with label, value, and disabled state
                      options={sectionOptions?.map((key) => {
                        const {
                          filterLabel: label,
                          isDisabled: disabled,
                          disabledFilterInfoMessage,
                          labelTooltipText,
                        } = filterConfig[key];
                        return {
                          label,
                          value: key,
                          disabled: disabled,
                          disabledFilterInfoMessage,
                          labelTooltipText,
                        };
                      })}
                      onItemClick={(option) => {
                        onChange(option);
                        onClose();
                      }}
                      previousSelectedFilter={previousSelectedFilter}
                    />
                  );
                }
              );
            })()}
          </>
        )}
        {filteredCustomFields?.length > 0 && (
          <AccordionWrapper
            title="Custom filters"
            count={filteredCustomFields.length}
            options={filteredCustomFields || []}
            onItemClick={handleCustomFilterChange}
            isLoading={customFilters?.isLoading}
            isEmptyText="No matching Filters found."
            previousSelectedFilter={previousSelectedFilter}
          />
        )}
      </Flex>
    );
  };

  return (
    <Box ref={ref}>
      <Popover isOpen={isOpen}>
        <PopoverTrigger>{renderTrigger()}</PopoverTrigger>
        {/* On narrow screens the dropdown fills the viewport: the fixed 350px
            plus the 75px offset (which nudges it past the "By" label on
            desktop) totalled 425px and ran off-screen. Desktop unchanged. */}
        <PopoverContent
          ml={{ base: '0', md: '75px' }}
          width={{ base: '85vw', md: '350px' }}
          maxWidth="100vw"
          borderRadius="sm"
        >
          <PopoverBody p={0}>
            <Accordion allowMultiple={true} defaultIndex={[0, 1]}>
              {showSearchInput && renderSearchInput()}
              {renderFilterOptions()}
            </Accordion>
          </PopoverBody>
        </PopoverContent>
      </Popover>
    </Box>
  );
};
